/**
 * ShippingModePanel — Admin UI Sprint UX Cleanup
 *
 * Card untuk memilih mode pengiriman:
 *  - realtime (Jubelio) — ongkir otomatis dari Jubelio (default)
 *  - flat — tarif tetap manual yang diatur admin di section Flat Rate
 *
 * Backend endpoints:
 *  - GET  /api/admin/shipping/mode
 *  - PUT  /api/admin/shipping/mode  body: {mode:"realtime"|"flat"}
 */
import React, { useCallback, useEffect, useState } from "react";
import { Truck, Loader2, Save, Zap, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";

export default function ShippingModePanel() {
  const jubelioEnabled = useIsFeatureEnabled("integration.jubelio");
  const [mode, setMode] = useState("realtime");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shipping/mode");
      const raw = (data?.mode || "realtime").toLowerCase();
      // FEATURE GATE (integration.jubelio): kalau tenant tidak berhak realtime,
      // paksa mode "flat" — backend gate PUT juga menolak "realtime".
      setMode(jubelioEnabled ? raw : "flat");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [jubelioEnabled]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shipping/mode", { mode });
      toast.success("Mode pengiriman berhasil diperbarui.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="shipping-mode-panel">
      <div>
        <h3 className="font-bold flex items-center gap-1.5 text-[#1a1a1a]">
          <Truck size={16} /> Mode Pengiriman
        </h3>
        <p className="text-xs text-[#5C5C5C] mt-0.5">
          Pilih sistem perhitungan ongkir yang ditampilkan ke customer saat checkout.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5C5C5C] py-4 justify-center">
          <Loader2 size={14} className="animate-spin" /> Memuat...
        </div>
      ) : (
        <div className="space-y-2">
          {jubelioEnabled && (
          <label
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${mode === "realtime" ? "border-[#E85D04] bg-[#FFEDD5]/30" : "border-[#E5E5E5]"}`}
            data-testid="shipping-mode-realtime-row"
          >
            <input
              type="radio"
              name="shipping-mode"
              value="realtime"
              checked={mode === "realtime"}
              onChange={() => setMode("realtime")}
              className="mt-1"
              data-testid="shipping-mode-realtime"
            />
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1.5 text-[#1a1a1a]">
                <Zap size={13} className="text-blue-600" /> Ongkir Realtime (Jubelio)
              </div>
              <div className="text-xs text-[#5C5C5C] mt-0.5">
                Menggunakan tarif otomatis dari Jubelio. Customer melihat kurir lengkap (Instant, Regular, Same Day, Next Day) berdasarkan area pengiriman & berat paket.
              </div>
            </div>
          </label>
          )}

          <label
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${mode === "flat" ? "border-[#E85D04] bg-[#FFEDD5]/30" : "border-[#E5E5E5]"}`}
            data-testid="shipping-mode-flat-row"
          >
            <input
              type="radio"
              name="shipping-mode"
              value="flat"
              checked={mode === "flat"}
              onChange={() => setMode("flat")}
              className="mt-1"
              data-testid="shipping-mode-flat"
            />
            <div className="flex-1">
              <div className="font-bold text-sm flex items-center gap-1.5 text-[#1a1a1a]">
                <ClipboardList size={13} className="text-emerald-600" /> Ongkir Flat Manual
              </div>
              <div className="text-xs text-[#5C5C5C] mt-0.5">
                Menggunakan tarif tetap yang Anda atur di section <b>Tarif Flat Manual</b>.
                Cocok sebagai mode darurat saat Jubelio bermasalah, atau untuk operasional sederhana.
              </div>
            </div>
          </label>
        </div>
      )}

      <Button
        onClick={save}
        disabled={loading || saving}
        className="bg-[#E85D04] hover:bg-[#DC2F02] rounded-xl"
        data-testid="shipping-mode-save-btn"
      >
        <Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Mode Pengiriman"}
      </Button>
    </Card>
  );
}
